import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTagsStore } from '@/stores/tagsStore';
import { useHistoryStore, createHistoryEntry } from '@/stores/historyStore';
import { executeTagExtraction } from '@/services/tagsApi';
import { TextArea } from '@/components/TextArea';
import { Button } from '@/components/Button';
import { ChipEditor } from '@/components/ChipEditor/ChipEditor';
import { ResultPanel } from '@/components/ResultPanel/ResultPanel';
import { DebugPanel } from '@/components/DebugPanel/DebugPanel';
import { showToast } from '@/components/Toast/Toast';
import { readMarkdownFile, isValidMarkdownFile, readValuesFile, isValidValuesFile } from '@/utils/parseValuesFile';
import type { FileUploadMode, TagExtractionResponse } from '@/types/api';
import styles from './EconomicSectorPage.module.css';

export function EconomicSectorPage() {
  const { t } = useTranslation();
  const markdownInputRef = useRef<HTMLInputElement>(null);
  const valuesInputRef = useRef<HTMLInputElement>(null);
  const [uploadMode, setUploadMode] = useState<FileUploadMode>('replace');

  const {
    economic_sector,
    setEconomicSectorLessonMarkdown,
    setEconomicSectorValidValues,
    setEconomicSectorLoading,
    setEconomicSectorError,
    setEconomicSectorResponse,
    loadEconomicSectorDefaults,
    applyEconomicSectorValuesFromFile,
    clearEconomicSector,
  } = useTagsStore();

  const { addEntry } = useHistoryStore();

  const handleSubmit = useCallback(async () => {
    if (!economic_sector.lesson_markdown.trim()) {
      showToast('error', t('errors.lessonRequired'));
      return;
    }
    if (economic_sector.valid_values.length === 0) {
      showToast('error', t('errors.validValuesRequired'));
      return;
    }

    setEconomicSectorLoading(true);

    const result = await executeTagExtraction(
      'economic_sector',
      economic_sector.lesson_markdown,
      economic_sector.valid_values
    );

    addEntry(
      createHistoryEntry(
        'economic_sector',
        result.requestPayload,
        result.ok ? (result.data as TagExtractionResponse) : null,
        result.responseHeaders || {},
        result.error || null
      )
    );

    if (result.ok && result.data) {
      setEconomicSectorResponse(result.data as TagExtractionResponse, result.traceId || null);
      showToast('success', t('toast.submitSuccess'));
    } else {
      setEconomicSectorError(result.error || t('errors.unknownError'));
    }
  }, [economic_sector, t, setEconomicSectorLoading, setEconomicSectorResponse, setEconomicSectorError, addEntry]);

  const handleMarkdownUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidMarkdownFile(file)) {
        showToast('error', t('errors.invalidFileFormat'));
        return;
      }

      try {
        const content = await readMarkdownFile(file);
        setEconomicSectorLessonMarkdown(content);
        showToast('success', t('toast.loadedFromFile'));
      } catch {
        showToast('error', t('errors.fileReadError'));
      }

      if (markdownInputRef.current) markdownInputRef.current.value = '';
    },
    [t, setEconomicSectorLessonMarkdown]
  );

  const handleValuesUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidValuesFile(file)) {
        showToast('error', t('errors.invalidValuesFileFormat'));
        return;
      }

      try {
        const values = await readValuesFile(file);
        applyEconomicSectorValuesFromFile(uploadMode, values);
        showToast('success', t('toast.valuesLoaded', { count: values.length }));
      } catch {
        showToast('error', t('errors.fileReadError'));
      }

      if (valuesInputRef.current) valuesInputRef.current.value = '';
    },
    [t, uploadMode, applyEconomicSectorValuesFromFile]
  );

  const isSubmitDisabled =
    !economic_sector.lesson_markdown.trim() ||
    economic_sector.valid_values.length === 0 ||
    economic_sector.loading;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('pages.economicSector.title')}</h1>
        <p className={styles.description}>{t('pages.economicSector.description')}</p>
      </header>

      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className={styles.field}>
          <TextArea
            label={t('form.lessonMarkdown')}
            value={economic_sector.lesson_markdown}
            onChange={(e) => setEconomicSectorLessonMarkdown(e.target.value)}
            placeholder={t('form.lessonMarkdownPlaceholder')}
            rows={10}
            disabled={economic_sector.loading}
          />
          <div className={styles.fileUpload}>
            <input ref={markdownInputRef} type="file" accept=".md,.markdown" onChange={handleMarkdownUpload} className={styles.fileInput} id="markdown-upload-es" />
            <label htmlFor="markdown-upload-es" className={styles.fileLabel}>{t('form.uploadMarkdown')}</label>
          </div>
        </div>

        <div className={styles.field}>
          <ChipEditor
            label={t('form.validValues')}
            values={economic_sector.valid_values}
            onChange={setEconomicSectorValidValues}
            disabled={economic_sector.loading}
            error={economic_sector.valid_values.length === 0 ? t('errors.validValuesRequired') : undefined}
          />
          <div className={styles.valuesActions}>
            <Button type="button" variant="secondary" size="small" onClick={loadEconomicSectorDefaults} disabled={economic_sector.loading}>
              {t('form.loadDefaults')}
            </Button>
            <div className={styles.uploadControls}>
              <select value={uploadMode} onChange={(e) => setUploadMode(e.target.value as FileUploadMode)} className={styles.modeSelect}>
                <option value="replace">{t('form.replace')}</option>
                <option value="append">{t('form.append')}</option>
              </select>
              <input ref={valuesInputRef} type="file" accept=".txt" onChange={handleValuesUpload} className={styles.fileInput} id="values-upload-es" />
              <label htmlFor="values-upload-es" className={styles.fileLabel}>{t('form.uploadValues')}</label>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="submit" disabled={isSubmitDisabled} loading={economic_sector.loading}>{t('common.submit')}</Button>
          <Button type="button" variant="secondary" onClick={clearEconomicSector} disabled={economic_sector.loading}>{t('common.clear')}</Button>
        </div>
      </form>

      <ResultPanel response={economic_sector.lastResponse} traceId={economic_sector.lastTraceId} error={economic_sector.error} loading={economic_sector.loading} />
      <DebugPanel
        requestPayload={economic_sector.lastResponse ? { lesson_markdown: economic_sector.lesson_markdown, valid_values: economic_sector.valid_values } : null}
        responsePayload={economic_sector.lastResponse}
        responseHeaders={economic_sector.lastTraceId ? { 'x-request-id': economic_sector.lastTraceId } : {}}
      />
    </div>
  );
}
